import React from "react";
import { useTranslation } from "react-i18next";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Typography,
} from "@mui/material";

/**
 * Legenda del CSV della "Qualità dell'aria di gruppo": colonne, codici di esito e valori di
 * posizione. Nomi di colonne e codici restano letterali, come nel file. L'esito `sosta` sostituisce
 * `fuori_tracciato`, che col percorso di gruppo non esiste piu'.
 */
export default function GroupAirQualityCsvLegend({ open, onClose }) {
    const { t } = useTranslation();

    const columns = [
        ["data_ora", t("groupAirQualityCsvColDataOra", "Data e ora del campione, nel fuso dell'iniziativa.")],
        ["lat, lng", t("groupAirQualityCsvColLatLng", "Posizione assegnata al campione. Per i campioni a posizione incerta è l'ultimo punto GPS prima del buco.")],
        ["utente", t("groupAirQualityCsvColUtente", "Nome utente del partecipante.")],
        ["sessione", t("groupAirQualityCsvColSessione", "Identificativo della sessione.")],
        ["serie", t("groupAirQualityCsvColSerie", "Serie del sensore: oaq (ozono e AQI) o pmx (particolato).")],
        ["grandezza", t("groupAirQualityCsvColGrandezza", "Grandezza misurata: ozone_density, aqi_epa, pm1, pm2_5, pm10.")],
        ["valore", t("groupAirQualityCsvColValore", "Valore usato da mappa e medie: ozono in ppb, particolato in µg/m³, AQI come indice EPA 0-500, cioè il valore del dispositivo moltiplicato per 2.")],
        ["valore_grezzo", t("groupAirQualityCsvColValoreGrezzo", "Valore come arriva dal sensore: l'ozono è in centesimi di ppb, l'AQI è la metà dell'indice EPA.")],
        ["unita", t("groupAirQualityCsvColUnita", "Unità di misura del valore.")],
        ["esito", t("groupAirQualityCsvColEsito", "Esito dei filtri della mappa.")],
        ["posizione", t("groupAirQualityCsvColPosizione", "Da dove viene la posizione del campione.")],
        ["lat_stimata, lng_stimata", t("groupAirQualityCsvColLatLngStimata", "Posizione stimata lungo il percorso ricostruito; compilate solo quando posizione = stimata.")],
    ];
    const outcomes = [
        ["usato", t("groupAirQualityCsvOutcomeUsato", "Usato nelle medie della mappa.")],
        ["sosta", t("groupAirQualityCsvOutcomeSosta", "Preso durante una sosta di almeno 5 minuti, escluso dalla mappa.")],
        ["fuori_tetto", t("groupAirQualityCsvOutcomeFuoriTetto", "Oltre il massimo fisicamente plausibile.")],
        ["fuori_scala", t("groupAirQualityCsvOutcomeFuoriScala", "Oltre 3 volte, o sotto un terzo, la mediana dei campioni vicini.")],
        ["posizione_incerta", t("groupAirQualityCsvOutcomePosizioneIncerta", "Preso durante un buco GPS: la posizione non è nota ed è escluso dalla mappa.")],
    ];
    const positions = [
        ["rilevata", t("groupAirQualityCsvPositionRilevata", "Posizione dal GPS.")],
        ["incerta", t("groupAirQualityCsvPositionIncerta", "Durante un buco GPS, senza una stima accettata.")],
        ["stimata", t("groupAirQualityCsvPositionStimata", "Durante un buco GPS, posizionato sul percorso ricostruito (vedi lat_stimata e lng_stimata).")],
    ];

    const section = (title, rows) => (
        <>
            <Typography variant="subtitle2" sx={{ mt: 2 }}>
                {title}
            </Typography>
            <Table size="small">
                <TableBody>
                    {rows.map(([code, text]) => (
                        <TableRow key={code}>
                            <TableCell sx={{ fontFamily: "monospace", whiteSpace: "nowrap", verticalAlign: "top" }}>{code}</TableCell>
                            <TableCell>{text}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </>
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{t("groupAirQualityCsvLegend", "Legenda CSV")}</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2">
                    {t("groupAirQualityCsvLegendIntro", "Una riga per campionatura: ogni campione dà una riga per ciascuna grandezza misurata. Separatore: punto e virgola.")}
                </Typography>
                {section(t("groupAirQualityCsvLegendColumns", "Colonne"), columns)}
                {section(t("groupAirQualityCsvLegendOutcomes", "Codici di esito"), outcomes)}
                {section(t("groupAirQualityCsvLegendPositions", "Valori di posizione"), positions)}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t("close")}</Button>
            </DialogActions>
        </Dialog>
    );
}
